import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import '../styles/index.css';
import useGraderFlow from '../hooks/useGraderFlow';
import SearchSection from './SearchSection';
import ScanningPhase from './ScanningPhase';
import PreviewPhase from './PreviewPhase';
import UnlockPhase from './UnlockPhase';
import FullReportPhase from './FullReportPhase';
import ErrorPhase from './ErrorPhase';

const EASE = [0.22, 1, 0.36, 1];

const GraderShell = () => {
  const {
    query,
    suggestions,
    showDropdown,
    setShowDropdown,
    loadingSuggestions,
    searchError,
    scanFailure,
    phase,
    scanStepIndex,
    selectedBusiness,
    scanVisuals,
    preview,
    fullReport,
    unlocking,
    unlockStep,
    unlockPhone,
    setUnlockPhone,
    otpCode,
    setOtpCode,
    otpStatusMessage,
    otpResendCooldownMs,
    unlockError,
    otpSending,
    otpChecking,
    prospectSubmitting,
    prospectName,
    setProspectName,
    prospectEmail,
    setProspectEmail,
    prospectRelationship,
    setProspectRelationship,
    prospectOptIn,
    setProspectOptIn,
    prospectConsentAccepted,
    setProspectConsentAccepted,
    previewRevealStage,
    wrapperRef,
    handleSearchChange,
    onSubmit,
    onSelectSuggestion,
    currentScanStepLabel,
    scanProgress,
    visibleScanActivity,
    mapEmbedUrl,
    mapZoomTransform,
    pillarCards,
    beginUnlockFlow,
    handleSendOtp,
    otpCooldownSeconds,
    handleVerifyOtp,
    handleSubmitProspect,
    atRiskPillars,
    winningPillars,
    resetAll,
    SCAN_STEPS,
    pendingBootstrapBusiness,
    pendingBootstrapError,
    confirmPendingBootstrap,
    cancelPendingBootstrap
  } = useGraderFlow();

  const innerClass = `vlg__inner${phase === 'full' || phase === 'scanning' ? ' vlg__inner--wide' : phase === 'preview' ? ' vlg__inner--med' : ''}`;

  return (
    <section className="vlg">
      <div className={innerClass}>
        {phase === 'search' && (
          <motion.div
            className="vlg-landing"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <span className="vlg-eyebrow">
              <span className="vlg-eyebrow__dot" aria-hidden="true" />
              Free online visibility scan
            </span>
            <h1 className="vlg-headline">
              See where your business goes <em>invisible</em>.
            </h1>
            <p className="vlg-subhead">
              One scan checks your Google profile, reviews, website, and how AI assistants
              describe you - then shows where local competitors are beating you, and what to fix first.
            </p>

            <SearchSection
              wrapperRef={wrapperRef}
              onSubmit={onSubmit}
              query={query}
              handleSearchChange={handleSearchChange}
              suggestions={suggestions}
              loadingSuggestions={loadingSuggestions}
              setShowDropdown={setShowDropdown}
              resetAll={resetAll}
              showDropdown={showDropdown}
              onSelectSuggestion={onSelectSuggestion}
              searchError={searchError}
            />

            {pendingBootstrapBusiness && (
              <div className="vlg-confirm" role="dialog" aria-label="Confirm scan">
                <div className="vlg-confirm__copy">
                  <span className="vlg-confirm__label">Run free visibility scan for</span>
                  <span className="vlg-confirm__name">{pendingBootstrapBusiness.name}</span>
                  {pendingBootstrapBusiness.city && (
                    <span className="vlg-confirm__meta">{pendingBootstrapBusiness.city}</span>
                  )}
                </div>
                <div className="vlg-confirm__actions">
                  <button type="button" className="vlg-btn" onClick={confirmPendingBootstrap}>
                    Run scan
                  </button>
                  <button type="button" className="vlg-btn vlg-btn--ghost" onClick={cancelPendingBootstrap}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {pendingBootstrapError && !pendingBootstrapBusiness && (
              <p className="vlg-note vlg-note--error" style={{ textAlign: 'center' }}>{pendingBootstrapError}</p>
            )}
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {phase === 'scanning' && (
            <ScanningPhase
              key="scanning"
              selectedBusiness={selectedBusiness}
              currentScanStepLabel={currentScanStepLabel}
              scanProgress={scanProgress}
              visibleScanActivity={visibleScanActivity}
              scanVisuals={scanVisuals}
              mapEmbedUrl={mapEmbedUrl}
              mapZoomTransform={mapZoomTransform}
              SCAN_STEPS={SCAN_STEPS}
              scanStepIndex={scanStepIndex}
            />
          )}

          {phase === 'preview' && preview && (
            <PreviewPhase
              key="preview"
              preview={preview}
              previewRevealStage={previewRevealStage}
              pillarCards={pillarCards}
              beginUnlockFlow={beginUnlockFlow}
            />
          )}

          {phase === 'booking' && (
            <UnlockPhase
              key="booking"
              unlockStep={unlockStep}
              unlockPhone={unlockPhone}
              setUnlockPhone={setUnlockPhone}
              handleSendOtp={handleSendOtp}
              otpSending={otpSending}
              otpResendCooldownMs={otpResendCooldownMs}
              otpCooldownSeconds={otpCooldownSeconds}
              otpStatusMessage={otpStatusMessage}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              handleVerifyOtp={handleVerifyOtp}
              otpChecking={otpChecking}
              prospectName={prospectName}
              setProspectName={setProspectName}
              prospectEmail={prospectEmail}
              setProspectEmail={setProspectEmail}
              prospectRelationship={prospectRelationship}
              setProspectRelationship={setProspectRelationship}
              prospectOptIn={prospectOptIn}
              setProspectOptIn={setProspectOptIn}
              prospectConsentAccepted={prospectConsentAccepted}
              setProspectConsentAccepted={setProspectConsentAccepted}
              handleSubmitProspect={handleSubmitProspect}
              prospectSubmitting={prospectSubmitting}
              unlocking={unlocking}
              unlockError={unlockError}
            />
          )}

          {phase === 'full' && fullReport && (
            <FullReportPhase
              key="full"
              fullReport={fullReport}
              selectedBusiness={selectedBusiness}
              atRiskPillars={atRiskPillars}
              winningPillars={winningPillars}
              resetAll={resetAll}
            />
          )}

          {phase === 'error' && (
            <ErrorPhase key="error" searchError={searchError} scanFailure={scanFailure} resetAll={resetAll} />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default GraderShell;
